import React, { useEffect, useMemo, useRef, useState } from "react";

import { ChartData, ChartOptions, ChartType, Color, Chart as TypeChart } from "chart.js";
import Zoom from "chartjs-plugin-zoom";
import { Moment } from "moment";
import { Chart, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { Spin } from "antd";

import { Platform, ResolutionType } from "../../../../store/common_types";
import { HostChartItem, HostsMetric } from "../../../../store/hosts/types";
import { minRangeWindowMap, resolutionUnitMap, useRangeLimit } from "../../../../utils/resolution";

export interface HostsChartProps {
  items: HostChartItem[];
  metric: HostsMetric;
  rangeFrom: Moment;
  rangeTo: Moment;
  loading: boolean;
  resolution: ResolutionType;
}

interface PlatformColor {
  platform: Platform;
  backgroundColor: Color;
  borderColor: Color;
}

const platformsColor: PlatformColor[] = [
  { platform: "web", backgroundColor: "rgb(4, 201, 135)", borderColor: "rgba(4, 201, 135, .4)" },
  { platform: "android", backgroundColor: "rgb(184, 106, 61)", borderColor: "rgba(184, 106, 61, .4)" },
  { platform: "iphone", backgroundColor: "rgb(96, 57, 231)", borderColor: "rgba(96, 57, 231, .4)" },
  { platform: "ipad", backgroundColor: "rgb(70, 170, 238)", borderColor: "rgba(70, 170, 238, .4)" },
  { platform: "mobile", backgroundColor: "rgb(222, 169, 55)", borderColor: "rgba(222, 169, 55, .4)" },
  { platform: "other", backgroundColor: "rgb(234, 131, 160)", borderColor: "rgba(234, 131, 160, .2)" },
];

export const HostsChart: React.FC<HostsChartProps> = (props) => {
  const { t } = useTranslation(["hosts_page", "common"]);
  const limit = useRangeLimit(props.rangeFrom, props.rangeTo, "1d");
  const [data, setData] = useState<ChartData<"line", HostChartItem[]>>({ datasets: [] });
  const [options, setOptions] = useState<ChartOptions<"line">>({});
  const hostsChart = useRef<TypeChart<"line", HostChartItem[]>>();

  useEffect(() => {
    Chart.register(Zoom);
  }, []);

  useEffect(() => {
    setData({
      datasets: platformsColor.map(({ platform, backgroundColor, borderColor }) => ({
        label: t(`common:platforms.${platform}`),
        parsing: { yAxisKey: platform, xAxisKey: "date" },
        fill: false,
        data: props.items,
        backgroundColor,
        borderColor,
        cubicInterpolationMode: "monotone",
        tension: 0.4,
      })),
    });
  }, [props.items, t]);

  useEffect(() => {
    setOptions({
      spanGaps: false,
      responsive: true,
      plugins: {
        datalabels: { display: false },
        legend: { position: "bottom" },
        tooltip: { position: "nearest" },
        zoom: {
          limits: { x: { ...limit, minRange: minRangeWindowMap["1d"] } },
          zoom: { enabled: minRangeWindowMap["1d"] < limit.max - limit.min, mode: "x", threshold: 1 },
        },
      },
      interaction: { intersect: false, mode: "index" },
      scales: {
        y: { beginAtZero: true, title: { display: true, text: t(`legends.${props.metric}`) } },
        x: {
          type: "time",
          ...limit,
          time: {
            isoWeekday: 1,
            displayFormats: { day: "YYYY-MM-DD", month: "YYYY-MM" },
            ...resolutionUnitMap[props.resolution],
          },
          offset: false,
          ticks: {
            maxRotation: 0,
            align: "center",
          },
        },
      },
    });
  }, [limit, props.metric, props.resolution, t]);

  useEffect(() => {
    const chart = hostsChart.current;
    if (chart !== undefined) {
      chart.options = options;
      chart.data = data;
      chart.update("none");
    }
  }, [data, options]);

  const lineChart = useMemo(() => {
    return <Line ref={hostsChart} data={{ datasets: [] }} height={75} type={"line" as ChartType} />;
  }, []);

  return <Spin spinning={props.loading}>{lineChart}</Spin>;
};
